import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { 
  getLocalizedValue, 
  formatDate, 
  formatNumber, 
  formatCurrency,
  getRelativeTime 
} from '../utils/i18nHelpers';

/**
 * 用于处理API返回的多语言内容的自定义Hook
 * @returns {Object} 包含本地化相关的工具函数
 */
export const useLocalizedContent = () => {
  const { i18n } = useTranslation();
  
  /**
   * 获取本地化的值
   */
  const getLocalized = useCallback((multiLangObj, fallback = 'zh_CN') => {
    return getLocalizedValue(multiLangObj, i18n.language, fallback);
  }, [i18n.language]);
  
  /**
   * 当前语言代码（API格式：zh_CN）
   */
  const currentLang = i18n.language.replace('-', '_');
  
  /**
   * 当前语言代码（UI格式：zh-CN）
   */
  const currentLocale = i18n.language;
  
  /**
   * 格式化日期
   */
  const localizedDate = useCallback((date, options) => {
    return formatDate(date, i18n.language, options);
  }, [i18n.language]);
  
  /**
   * 格式化数字
   */
  const localizedNumber = useCallback((number, options) => {
    return formatNumber(number, i18n.language, options);
  }, [i18n.language]);
  
  /**
   * 格式化货币
   */
  const localizedCurrency = useCallback((amount, currency = 'SGD') => {
    return formatCurrency(amount, currency, i18n.language);
  }, [i18n.language]);
  
  /**
   * 获取相对时间
   */
  const localizedRelativeTime = useCallback((date) => {
    return getRelativeTime(date, i18n.language);
  }, [i18n.language]);
  
  /**
   * 检查是否为RTL语言
   */
  const isRTL = ['ar', 'he', 'fa'].includes(i18n.language.split('-')[0]);
  
  return {
    getLocalized,
    currentLang,
    currentLocale,
    formatDate: localizedDate,
    formatNumber: localizedNumber,
    formatCurrency: localizedCurrency,
    getRelativeTime: localizedRelativeTime,
    isRTL
  };
};
